import React, { useEffect, useMemo, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import { createApiClient } from '../lib/apiClient';
import { useUserId } from "../UserIdContext";
import {
  mergeDistinctStrings,
  normalizeStringList,
  pickFirstStringListValue,
  resolveDepositoActual,
} from "./unidadesNegocioApi";
import "./ingreso.css";
import "./ingresomk.css";
import "./usuarios.css";
import "./modificarIngreso.css";

const api = createApiClient({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:3220",
  headers: { "Content-Type": "application/json" },
});

const initialForm = {
  cliente: "",
  fecha: "",
  ubicacion: "",
  planta: "",
  unidadNegocio: "",
  nroRemito: "",
  fcGrupoProveedor: "",
  codigo: "",
  detalleProducto: "",
  nroFc: "",
  cantPorFc: "",
  lote: "",
  fechaElaboracion: "",
  fechaVencimiento: "",
  idInterno: "",
  tipoEmbalaje: "",
  pesoMedidas: "",
  nroPallet: "",
  unidadesPorPallet: "",
  recintoSenasa: "",
  ingresoPesa: "no", // "si" / "no"
  observacionesIngreso: "",
  condicion: "OK", // OK / ROTAS
};

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const normalizarFiltros = (arr) => {
  if (!Array.isArray(arr)) return [];
  const set = new Set();
  arr.forEach((v) => {
    const s = String(v || "").trim();
    if (s) set.add(s);
  });
  return Array.from(set);
};

const normalizarNombreFiltro = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const resolveClientePlantas = (cliente) =>
  mergeDistinctStrings(
    normalizeStringList(cliente?.plantas),
    normalizeStringList(cliente?.procedencia),
    normalizeStringList(cliente?.planta)
  );

const resolveClientePlanta = (cliente) => resolveClientePlantas(cliente)[0] || "";

const mapIngresoPallet = (p) => {
  const producto = p?.producto || {};
  const lote = p?.lote || {};
  const palletId = p?._id ? String(p._id) : p?.palletId ? String(p.palletId) : "";

  return {
    palletId: palletId || undefined,
    fcGrupoProveedor: p?.fcGrupoProveedor || producto.detalleProducto || "",
    codigo: p?.codigo || producto.codProducto || "",
    detalleProducto:
      p?.detalleProductoTexto || producto.nombreProducto || producto.detalleProducto || "",
    nroFc: p?.nroFc || "",
    cantPorFc: p?.cantPorFc ?? producto.cantidadxfc ?? "",
    lote: p?.loteTexto || lote.nroLote || "",
    fechaElaboracion: toDateInput(p?.fechaElaboracion || lote.fechaFab),
    fechaVencimiento: toDateInput(p?.fechaVencimiento || lote.fechaVto),
    idInterno: p?.idInterno || p?.identificacion || "",
    tipoEmbalaje: p?.tipoEmbalaje || "",
    pesoMedidas: p?.pesoMedidas || producto.peso || "",
    nroPallet: p?.nPallet || p?.nroPallet || "",
    unidadesPorPallet: p?.unidadesPorPallet ?? p?.cantidad ?? "",
    recintoSenasa: p?.recintoSenasa || p?.senasaCodigo || "",
    ingresoPesa: p?.ingresoPesa || "no",
    observacionesIngreso: p?.observacionesIngreso || "",
    condicion: p?.condicion || (p?.danios?.daniosIn ? "ROTAS" : "OK"),
    motivoDanioIn: p?.motivoDanioIn || p?.danios?.obsIn || "",
    palletOriginal: p?.palletOriginal || "",
    esDuplicado: !!p?.esDuplicado,
    duplicarPallet: !!p?.duplicarPallet,
    cuentaPosicion: p?.cuentaPosicion !== false,
  };
};

const getPalletDetailItems = () =>
  [
    { label: "ID interno", field: "idInterno" },
    { label: "Nro. pallet", field: "nroPallet" },
    { label: "FC / Grupo / Proveedor", field: "fcGrupoProveedor" },
    { label: "Codigo", field: "codigo" },
    { label: "Detalle de producto", field: "detalleProducto" },
    { label: "Nro. FC", field: "nroFc" },
    { label: "Cantidad por FC", field: "cantPorFc", type: "number" },
    { label: "Lote", field: "lote" },
    { label: "Fecha elaboracion", field: "fechaElaboracion", type: "date" },
    { label: "Fecha vencimiento", field: "fechaVencimiento", type: "date" },
    { label: "Tipo de embalaje", field: "tipoEmbalaje" },
    { label: "Peso / Medidas", field: "pesoMedidas" },
    {
      label: "Unidades por pallet",
      field: "unidadesPorPallet",
      type: "number",
      min: 1,
    },
    { label: "Recinto SENASA", field: "recintoSenasa" },
    { label: "Condicion", field: "condicion", type: "condition" },
    { label: "Motivo de daño", field: "motivoDanioIn" },
    { label: "Observaciones", field: "observacionesIngreso" },
    { label: "Pallet original", field: "palletOriginal" },
    { label: "Es duplicado", field: "esDuplicado", type: "readonlyBoolean" },
    { label: "Duplicar pallet", field: "duplicarPallet", type: "boolean" },
    { label: "Cuenta posicion", field: "cuentaPosicion", type: "boolean" },
  ];

const ModificacionIngreso = () => {
  const { deposito } = useUserId();
  const depositoActual = resolveDepositoActual(deposito);

  // Estado del formulario principal
  const [form, setForm] = useState(initialForm);
  const [mantenerDatos, setMantenerDatos] = useState(true);
  const [replicarPor, setReplicarPor] = useState();
  const [pallets, setPallets] = useState([]);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [ingresoId, setIngresoId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [modalError, setModalError] = useState("");
  const [palletDetalleId, setPalletDetalleId] = useState(null);
  const [fechaVencimientoMasiva, setFechaVencimientoMasiva] = useState("");

  const [clienteQuery, setClienteQuery] = useState("");
  const [clientes, setClientes] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);
  const [clienteListOpen, setClienteListOpen] = useState(false);
  const [filtrosSel, setFiltrosSel] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [buscandoIngreso, setBuscandoIngreso] = useState(false);
  const [optsRemitos, setOptsRemitos] = useState([]);
  const [mostrarFiltrosModal, setMostrarFiltrosModal] = useState(false);

  const mostrarCampo = (nombre) =>
    filtrosSel.length === 0
      ? true
      : filtrosSel.some(
          (f) => normalizarNombreFiltro(f) === normalizarNombreFiltro(nombre)
        );

  const palletsGuardados = pallets.filter((p) => Boolean(p.palletId));
  const palletDetalle = palletsGuardados.find(
    (p) => p.palletId === palletDetalleId
  );
  const palletDetalleIndex = pallets.findIndex(
    (p) => p.palletId === palletDetalleId
  );
  const tieneValor = (value) =>
    value !== undefined && value !== null && String(value).trim() !== "";
  const tieneDatoGuardado = (...campos) =>
    palletsGuardados.some((p) => campos.some((campo) => tieneValor(p[campo])));
  const mostrarCampoConfiguradoOExistente = (nombre, existeEnBase) =>
    mostrarCampo(nombre) || existeEnBase;

  const showFechaIngreso = mostrarCampoConfiguradoOExistente(
    "Fecha ingreso",
    Boolean(ingresoId && form.fecha)
  );
  const showUbicacion = mostrarCampoConfiguradoOExistente(
    "Ubicacion",
    Boolean(ingresoId && form.ubicacion)
  );
  const showPlanta = mostrarCampoConfiguradoOExistente(
    "Planta",
    Boolean(ingresoId && form.planta)
  );
  const showUnidadNegocio = mostrarCampoConfiguradoOExistente(
    "Unidad de negocio",
    Boolean(ingresoId && form.unidadNegocio)
  );
  const showDetalleProducto = mostrarCampoConfiguradoOExistente(
    "Detalle de producto / N FC",
    tieneDatoGuardado(
      "fcGrupoProveedor",
      "detalleProducto",
      "nroFc",
      "cantPorFc"
    )
  );
  const showCodigo = mostrarCampoConfiguradoOExistente(
    "Codigo",
    tieneDatoGuardado("codigo")
  );
  const showLote = mostrarCampoConfiguradoOExistente(
    "Lote",
    tieneDatoGuardado("lote")
  );
  const showFechaElaboracion = mostrarCampoConfiguradoOExistente(
    "Fecha elaboracion",
    tieneDatoGuardado("fechaElaboracion")
  );
  const showFechaVencimiento = mostrarCampoConfiguradoOExistente(
    "Fecha vencimiento",
    tieneDatoGuardado("fechaVencimiento")
  );
  const showTipoEmbalaje =
    mostrarCampoConfiguradoOExistente(
      "Tipo de embalaje",
      tieneDatoGuardado("tipoEmbalaje")
    ) || mostrarCampo("Tipo embalaje");
  const showPesoMedidas = mostrarCampoConfiguradoOExistente(
    "Peso / Medidas",
    tieneDatoGuardado("pesoMedidas")
  );
  // Es un dato de stock esencial: no debe desaparecer por la configuracion
  // visual de filtros del cliente.
  const showUnidadesPallet = true;
  const showPrecinto = mostrarCampoConfiguradoOExistente(
    "Precinto SENASA",
    tieneDatoGuardado("recintoSenasa")
  );
  const showCondicion = mostrarCampoConfiguradoOExistente(
    "Condicion",
    tieneDatoGuardado("condicion", "motivoDanioIn")
  );
  const showObservaciones = mostrarCampoConfiguradoOExistente(
    "Observaciones",
    Boolean(ingresoId && form.observacionesIngreso) ||
      tieneDatoGuardado("observacionesIngreso")
  );

  const setFeedback = (text, tone = "info") => {
    setMensaje({ text, tone, ts: Date.now() });
    setTimeout(() => setMensaje(null), 4000);
  };

  // Maneja cambios en el formulario principal
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Maneja el checkbox de "mantener datos"
  const handleMantenerDatosChange = (e) => {
    setMantenerDatos(e.target.checked);
  };

  // Maneja el input "replicar ingreso por"
  const handleReplicarChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setReplicarPor(Number.isFinite(value) ? value : undefined);
  };

  const handleSelectCliente = (cliente) => {
    setClienteSel(cliente);
    setClienteQuery(cliente?.nombreCliente || "");
    setClienteListOpen(false);
    setFiltrosSel(normalizarFiltros(cliente?.Filtros));
    setForm({
      ...initialForm,
      cliente: cliente?.nombreCliente || "",
      ubicacion: pickFirstStringListValue(cliente?.ubicacion),
      planta: resolveClientePlanta(cliente),
      unidadNegocio: pickFirstStringListValue(cliente?.unidadNegocio),
      nroRemito: "",
    });
    setClientes([]);
    setIngresoId(null);
    setPallets([]);
    setPalletDetalleId(null);
  };

  // Agregar pallets al listado (botón "Continuar")
  const handleAgregarPallets = (e) => {
    e.preventDefault();

    if (!ingresoId) {
      setFeedback(
        "Primero seleccione un remito existente",
        "warn"
      );
      return;
    }

    const unidades = Number(form.unidadesPorPallet);
    if (!Number.isFinite(unidades) || unidades <= 0) {
      setFeedback("Ingrese una cantidad de unidades por pallet mayor a cero", "warn");
      return;
    }

    const cantidad = replicarPor || 1;

    // Intento de numerar los pallets si nroPallet es numérico
    let basePalletNumber = parseInt(form.nroPallet, 10);
    const esBaseNumerica = !isNaN(basePalletNumber);

    const nuevosPallets = Array.from({ length: cantidad }, (_, index) => {
      const nroPalletCalculado = esBaseNumerica
        ? String(basePalletNumber + index)
        : form.nroPallet;

      return {
        cliente: form.cliente,
        fecha: form.fecha,
        ubicacion: form.ubicacion,
        planta: form.planta,
        unidadNegocio: form.unidadNegocio,
        nroRemito: form.nroRemito,

        fcGrupoProveedor: form.fcGrupoProveedor,
        codigo: form.codigo,
        detalleProducto: form.detalleProducto,
        nroFc: form.nroFc,
        cantPorFc: form.cantPorFc,
        lote: form.lote,
        fechaElaboracion: form.fechaElaboracion,
        fechaVencimiento: form.fechaVencimiento,
        idInterno: form.idInterno,
        tipoEmbalaje: form.tipoEmbalaje,
        pesoMedidas: form.pesoMedidas,
        nroPallet: nroPalletCalculado,
        unidadesPorPallet: form.unidadesPorPallet,
        recintoSenasa: form.recintoSenasa,
        ingresoPesa: form.ingresoPesa,
        observacionesIngreso: form.observacionesIngreso,
        condicion: form.condicion,

        motivoDanioIn: "",
        duplicarPallet: false,
        cuentaPosicion: true,
        esDuplicado: false,
        palletOriginal: "",
      };
    });

    setPallets((prev) => [...prev, ...nuevosPallets]);

    if (!mantenerDatos) {
      setForm((prev) => ({
        ...prev,
        fcGrupoProveedor: "",
        codigo: "",
        detalleProducto: "",
        nroFc: "",
        cantPorFc: "",
        lote: "",
        fechaElaboracion: "",
        fechaVencimiento: "",
        idInterno: "",
        tipoEmbalaje: "",
        pesoMedidas: "",
        nroPallet: "",
        unidadesPorPallet: "",
        recintoSenasa: "",
        condicion: "OK",
      }));
      setReplicarPor(1);
    }
  };

  // Maneja cambios en cada celda de la tabla de pallets
  const handlePalletChange = (index, field, value) => {
    setPallets((prev) =>
      prev.map((pallet, i) =>
        i === index
          ? {
              ...pallet,
              [field]: value,
            }
          : pallet
      )
    );
  };

  const handleAplicarFechaVencimientoTodos = () => {
    if (!fechaVencimientoMasiva) {
      setFeedback("Seleccione una fecha de vencimiento", "warn");
      return;
    }

    if (pallets.length === 0) {
      setFeedback("No hay pallets en el remito", "warn");
      return;
    }

    const confirmado = window.confirm(
      `Se reemplazara la fecha de vencimiento de los ${pallets.length} pallets del remito. ` +
        "Los distintos lotes pueden tener vencimientos diferentes. Desea continuar?"
    );
    if (!confirmado) return;

    setPallets((prev) =>
      prev.map((pallet) => ({
        ...pallet,
        fechaVencimiento: fechaVencimientoMasiva,
      }))
    );
    setFeedback(
      `Fecha aplicada a ${pallets.length} pallets. Finalice y confirme para guardarla.`,
      "ok"
    );
  };

  // Maneja cambios de checkbox en la tabla
  const handlePalletCheckbox = (index, field) => {
    setPallets((prev) => {
      const palletsCopy = [...prev];
      const palletActual = palletsCopy[index];

      // Valor nuevo del checkbox
      const nuevoValor = !palletActual[field];

      // Caso especial: duplicar pallet
      if (field === "duplicarPallet" && nuevoValor === true) {
        // Actualizo el pallet original marcando duplicarPallet = true
        palletsCopy[index] = {
          ...palletActual,
          duplicarPallet: true,
          esDuplicado: false,
        };

        // Creo el duplicado
        const palletDuplicado = {
          ...palletActual,
          palletId: undefined,
          duplicarPallet: false,
          esDuplicado: true,
          palletOriginal: palletActual.nroPallet || palletActual.palletOriginal,
          cuentaPosicion: false,
          unidadesPorPallet: 0,
        };

        // Inserto el duplicado en la fila de abajo
        palletsCopy.splice(index + 1, 0, palletDuplicado);

        return palletsCopy;
      }

      // Comportamiento normal para otros checkboxes
      palletsCopy[index] = {
        ...palletActual,
        [field]: nuevoValor,
      };

      return palletsCopy;
    });
  };

  // Eliminar una fila de pallets
  const handleEliminarPallet = (indexABorrar) => {
    setPallets((prev) => prev.filter((_, index) => index !== indexABorrar));
  };

  // cuando apreta enter en nro pallet, baja al nro pallet de la prox fila
  const handleEnterPallet = (e, index) => {
    if (e.key === "Enter") {
      const next = document.getElementById(`pallet-${index + 1}`);
      if (next) next.focus(); // existe -> enfoca siguiente
    }
  };

  // Resumen por lote para el modal
  const getResumenPorLote = () => {
    const resumen = {};

    pallets.forEach((p) => {
      const key = p.lote || "SIN LOTE";
      if (!resumen[key]) {
        resumen[key] = {
          lote: key,
          cantidadPallets: 0,
          totalUnidades: 0,
        };
      }
      if (p.cuentaPosicion !== false) {
        resumen[key].cantidadPallets += 1;
      }
      const unidades = parseFloat(p.unidadesPorPallet) || 0;
      resumen[key].totalUnidades += unidades;
    });

    return Object.values(resumen);
  };

  const totalPallets = pallets.length;
  const totalUnidades = pallets.reduce(
    (acc, p) => acc + (parseFloat(p.unidadesPorPallet) || 0),
    0
  );

  const payloadPallets = useMemo(
    () =>
      pallets.map((p) => ({
        ...p,
        cantPorFc: toNumber(p.cantPorFc),
        unidadesPorPallet: toNumber(p.unidadesPorPallet),
      })),
    [pallets]
  );

  const getSaveValidationError = () => {
    if (!ingresoId) {
      return "Primero seleccione un remito existente.";
    }
    if (!clienteSel?._id) return "Seleccione un cliente valido.";
    if (!String(form.nroRemito || "").trim()) return "Falta Nro de remito.";
    if (pallets.length === 0) return "Agregue al menos un pallet.";
    const palletSinUnidades = pallets.findIndex(
      (p) => p.cuentaPosicion !== false && !(Number(p.unidadesPorPallet) > 0)
    );
    if (palletSinUnidades >= 0) {
      const pallet = pallets[palletSinUnidades];
      const referencia = pallet.nroPallet || pallet.idInterno || palletSinUnidades + 1;
      return `El pallet ${referencia} debe tener una cantidad de unidades mayor a cero.`;
    }
    return "";
  };

  const handleRemitoSelection = (e) => {
    const nroRemito = e.target.value;
    setForm((prev) => ({ ...prev, nroRemito }));
    setFechaVencimientoMasiva("");
    setIngresoId(null);
    setPallets([]);
    setModalError("");
    setPalletDetalleId(null);

    if (nroRemito) buscarRemito(nroRemito);
  };

  const abrirModalResumen = () => {
    const validationError = getSaveValidationError();
    if (validationError) {
      setFeedback(validationError, "warn");
      return;
    }

    setModalError("");
    setMostrarModal(true);
  };

  // llamar a la api para modificar el ingreso
  const handleCompletarIngreso = async () => {
    const validationError = getSaveValidationError();
    if (validationError) {
      setModalError(validationError);
      setFeedback(validationError, "warn");
      return;
    }

    const payload = {
      clienteId: clienteSel._id,
      fechaIngreso: form.fecha,
      ubicacion: form.ubicacion,
      planta: form.planta,
      unidadNegocio: form.unidadNegocio,
      nroRemito: form.nroRemito,
      ingresoPesa: form.ingresoPesa,
      observacionesIngreso: form.observacionesIngreso,
      deposito: depositoActual,
      pallets: payloadPallets,
    };

    try {
      setGuardando(true);
      setModalError("");
      const { data } = await api.put(`/api/ingresos/${ingresoId}`, payload);
      if (data?.ok === false) {
        throw new Error(data?.error || data?.msg || "No pude actualizar el ingreso");
      }
      setFeedback("Ingreso actualizado", "ok");
      setMostrarModal(false);
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.msg ||
        error?.message ||
        "No pude actualizar el ingreso";
      setModalError(msg);
      setFeedback(msg, "error");
    } finally {
      setGuardando(false);
    }
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setModalError("");
  };

  // buscar remito por el nro y rellenar el formulario y pallets generados segun lo retornado
  const buscarRemito = async (remitoSeleccionado = "") => {
    const remito = String(remitoSeleccionado || form.nroRemito || "").trim();
    if (!remito) {
      setFeedback("Ingrese un nro de remito", "warn");
      return;
    }

    try {
      setBuscandoIngreso(true);
      const params = { nroRemito: remito, deposito: depositoActual };
      if (clienteSel?._id) params.clienteId = clienteSel._id;
      const { data } = await api.get("/api/ingresos/buscar", {
        params,
      });
      const ingreso = data?.data;
      if (!ingreso) {
        setFeedback("Ingreso no encontrado", "warn");
        setIngresoId(null);
        setPallets([]);
        return;
      }

      const ingresoCliente = ingreso.cliente || null;
      if (ingresoCliente) {
        setClienteSel(ingresoCliente);
        setClienteQuery(ingresoCliente.nombreCliente || "");
        setFiltrosSel(normalizarFiltros(ingresoCliente.Filtros));
      }

      const palletsResp = Array.isArray(ingreso.pallets) ? ingreso.pallets : [];
      const palletsMapeados = palletsResp.map(mapIngresoPallet);

      setIngresoId(ingreso._id);
      setModalError("");
      setForm((prev) => ({
        ...prev,
        cliente: ingresoCliente?.nombreCliente || prev.cliente || "",
        fecha: toDateInput(ingreso.fechaRemito || ingreso.fechaIngreso),
        ubicacion: ingreso.ubicacionTexto || "",
        planta: ingreso.planta || "",
        unidadNegocio: ingreso.unidadNegocio || "",
        nroRemito: ingreso.nroRemito || ingreso.remito || remito,
        ingresoPesa: ingreso.ingresoPesa || "no",
        observacionesIngreso: ingreso.observaciones || "",
        // El formulario corresponde a un pallet nuevo: no hereda datos de
        // ninguno de los pallets originales del remito.
        fcGrupoProveedor: "",
        codigo: "",
        detalleProducto: "",
        nroFc: "",
        cantPorFc: "",
        lote: "",
        fechaElaboracion: "",
        fechaVencimiento: "",
        tipoEmbalaje: "",
        pesoMedidas: "",
        unidadesPorPallet: "",
        recintoSenasa: "",
        condicion: "OK",
        idInterno: "",
        nroPallet: "",
      }));

      setPallets(palletsMapeados);
      setFeedback("Ingreso cargado", "ok");
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.msg ||
        "No pude buscar el ingreso";
      setFeedback(msg, "error");
      setIngresoId(null);
      setPallets([]);
    } finally {
      setBuscandoIngreso(false);
    }
  };

  useEffect(() => {
    if (!clienteListOpen) {
      setBuscandoCliente(false);
      return;
    }

    const queryActual = clienteQuery.trim();
    const clienteSeleccionado = String(clienteSel?.nombreCliente || "").trim();
    const q = clienteSel?._id && queryActual === clienteSeleccionado ? "" : queryActual;

    let active = true;
    const t = setTimeout(async () => {
      setBuscandoCliente(true);
      try {
        const { data } = q
          ? await api.get("/api/clientes/buscar", { params: { q } })
          : await api.get("/api/clientes/listar");
        if (active) setClientes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("GET clientes", err?.response || err);
        if (active) setClientes([]);
      } finally {
        if (active) setBuscandoCliente(false);
      }
    }, q ? 250 : 0);

    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [clienteQuery, clienteSel, clienteListOpen]);

  useEffect(() => {
    if (!clienteSel?._id) {
      setOptsRemitos([]);
      return;
    }
    let active = true;
    api
      .get("/api/pallets/opciones", {
        params: { clienteId: clienteSel._id, deposito: depositoActual },
      })
      .then(({ data }) => {
        if (!active) return;
        setOptsRemitos(data?.data?.remitos || []);
      })
      .catch(() => {
        if (!active) return;
        setOptsRemitos([]);
      });
    return () => {
      active = false;
    };
  }, [clienteSel, depositoActual]);

  // Opciones para los selects basadas en los valores guardados.
  const ubicaciones = mergeDistinctStrings(
    normalizeStringList(clienteSel?.ubicacion),
    form.ubicacion
  );
  const plantas = mergeDistinctStrings(resolveClientePlantas(clienteSel), form.planta);
  const unidadesNegocio = mergeDistinctStrings(
    normalizeStringList(clienteSel?.unidadNegocio),
    form.unidadNegocio
  );

  /*
  const handleCompletarFormulario = () => {
    // await axios.get("/api/cliente/completarFormulario", { idCliente });
  };
  */
  return (
    <div className="container my-4 modificar-ingreso-page">
      <h3 className="mb-3 text-white">Modificacion Ingreso</h3>

      {mensaje && (
        <div className={`estado-msg ${mensaje.tone || ""}`}>{mensaje.text}</div>
      )}

      <div className="card mb-3">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Cliente</label>
              <div className="cliente-autocomplete">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nombre o CUIT"
                  value={clienteQuery}
                  autoComplete="off"
                  onFocus={() => setClienteListOpen(true)}
                  onBlur={() => setClienteListOpen(false)}
                  onChange={(e) => {
                    setClienteQuery(e.target.value);
                    setClienteSel(null);
                    setClienteListOpen(true);
                    setFiltrosSel([]);
                    setOptsRemitos([]);
                    setIngresoId(null);
                    setPallets([]);
                    setForm(initialForm);
                    setPalletDetalleId(null);
                  }}
                />
                {buscandoCliente && (
                  <div className="cliente-estado">Buscando...</div>
                )}
                {!buscandoCliente && clienteListOpen && clientes.length === 0 && (
                  <div className="cliente-estado">
                    {clienteQuery.trim()
                      ? "No se encontraron clientes"
                      : "No hay clientes cargados"}
                  </div>
                )}
                {clienteListOpen && clientes.length > 0 && (
                  <div className="cliente-list">
                    {clientes.map((c) => (
                      <div
                        key={c._id}
                        className="cliente-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectCliente(c);
                        }}
                      >
                        <strong>{c.nombreCliente}</strong>{" "}
                        <span className="cliente-sub">
                          {c.codCliente} | CUIT {c.CUIT}
                        </span>
                        {Array.isArray(c.Filtros) && c.Filtros.length > 0 && (
                          <div className="cliente-sub">
                            Filtros: {normalizarFiltros(c.Filtros).join(", ")}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-6 d-flex align-items-end">
              {clienteSel ? (
                <div
                  className="chip"
                  role="button"
                  onClick={() => setMostrarFiltrosModal(true)}
                >
                  {filtrosSel.length
                    ? `Filtros: ${filtrosSel.join(", ")}`
                    : "Cliente sin filtros guardados"}
                </div>
              ) : (
                <div className="estado-msg info" style={{ margin: 0 }}>
                  Seleccione un cliente para cargar filtros y datos
                </div>
              )}
            </div>
          </div>

          <div className="row align-items-end">
            <div className="col-md-7">
              <label className="form-label">Remito disponible</label>
              <select
                className="form-select"
                value={form.nroRemito}
                onChange={handleRemitoSelection}
                disabled={!clienteSel || buscandoIngreso}
              >
                <option value="">
                  {!clienteSel
                    ? "Primero seleccione un cliente"
                    : optsRemitos.length > 0
                    ? "Seleccione un remito"
                    : "No hay remitos disponibles"}
                </option>
                {optsRemitos.map((r, i) => (
                  <option key={`${r}-${i}`} value={r}>
                    {r}
                  </option>
                ))}
                {form.nroRemito && !optsRemitos.includes(form.nroRemito) && (
                  <option value={form.nroRemito}>{form.nroRemito}</option>
                )}
              </select>
              {clienteSel && optsRemitos.length > 0 && (
                <small className="text-muted">
                  {optsRemitos.length} remito{optsRemitos.length === 1 ? "" : "s"} disponible{optsRemitos.length === 1 ? "" : "s"}
                </small>
              )}
            </div>
            <div className="col-md-5">
              <div
                className={`estado-msg ${ingresoId ? "ok" : "info"}`}
                style={{ margin: 0 }}
                aria-live="polite"
              >
                {buscandoIngreso
                  ? "Cargando ingreso..."
                  : ingresoId
                  ? "Ingreso cargado y listo para modificar"
                  : "Seleccione un remito para cargarlo"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FORMULARIO PRINCIPAL */}
      {ingresoId && (
      <form onSubmit={handleAgregarPallets}>
        <div className="card mb-3">
          <div className="card-header">
            <strong>Agregar pallets al remito {form.nroRemito}</strong>
            <div className="small text-muted">
              Complete los datos del nuevo pallet. Los pallets originales se
              editan desde &quot;Ver detalle&quot;.
            </div>
          </div>
          <div className="card-body">
            <div className="row mb-2">
              <div className="col-md-4">
                <label className="form-label">Cliente</label>
                <input
                  type="text"
                  className="form-control"
                  name="cliente"
                  value={form.cliente}
                  onChange={handleFormChange}
                  readOnly
                  disabled
                />
              </div>
              {showFechaIngreso && (
                <div className="col-md-2">
                  <label className="form-label">Fecha ingreso</label>
                  <input
                    type="date"
                    className="form-control"
                    name="fecha"
                    value={form.fecha}
                    onChange={handleFormChange}
                  />
                </div>
              )}
              {showUbicacion && (
                <div className="col-md-2">
                <label className="form-label">Ubicacion</label>
                <select
                  className="form-select"
                  name="ubicacion"
                  value={form.ubicacion}
                  onChange={handleFormChange}
                >
                  <option value="">Seleccionar...</option>
                  {ubicaciones.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
              )}
              {showPlanta && (
                <div className="col-md-2">
                  <label className="form-label">Procedencia / Planta</label>
                  <select
                    className="form-select"
                    name="planta"
                    value={form.planta}
                    onChange={handleFormChange}
                  >
                    <option value="">Seleccionar...</option>
                    {plantas.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {showUnidadNegocio && (
                <div className="col-md-2">
                  <label className="form-label">Unidad de negocio</label>
                  <select
                    className="form-select"
                    name="unidadNegocio"
                    value={form.unidadNegocio}
                    onChange={handleFormChange}
                  >
                    <option value="">Seleccionar...</option>
                    {unidadesNegocio.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="row mb-2">
              <div className="col-md-3">
                <label className="form-label">Nro. Remito</label>
                <input
                  type="text"
                  className="form-control"
                  name="nroRemito"
                  value={form.nroRemito}
                  onChange={handleFormChange}
                />
              </div>
              {showDetalleProducto && (
                <div className="col-md-3">
                  <label className="form-label">FC / Grupo / Proveedor</label>
                  <input
                    type="text"
                    className="form-control"
                    name="fcGrupoProveedor"
                    value={form.fcGrupoProveedor}
                    onChange={handleFormChange}
                  />
                </div>
              )}
              {showCodigo && (
                <div className="col-md-3">
                <label className="form-label">Codigo</label>
                <input
                  type="text"
                  className="form-control"
                  name="codigo"
                  value={form.codigo}
                  onChange={handleFormChange}
                />
                </div>
              )}
              {showDetalleProducto && (
                <div className="col-md-3">
                <label className="form-label">
                  Detalle de producto / Nro. FC
                </label>
                <input
                  type="text"
                  className="form-control mb-1"
                  placeholder="Detalle de producto"
                  name="detalleProducto"
                  value={form.detalleProducto}
                  onChange={handleFormChange}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nro. FC"
                  name="nroFc"
                  value={form.nroFc}
                  onChange={handleFormChange}
                />
              </div>
              )}
            </div>

            <div className="row mb-2">
              {showDetalleProducto && (
                <div className="col-md-2">
                  <label className="form-label">Cant. x FC</label>
                  <input
                    type="number"
                    className="form-control"
                    name="cantPorFc"
                    value={form.cantPorFc}
                    onChange={handleFormChange}
                  />
                </div>
              )}
              {showLote && (
                <div className="col-md-2">
                  <label className="form-label">Lote</label>
                  <input
                    type="text"
                    className="form-control"
                    name="lote"
                    value={form.lote}
                    onChange={handleFormChange}
                  />
                </div>
              )}
              {showFechaElaboracion && (
              <div className="col-md-2">
                <label className="form-label">Fecha elaboracion</label>
                <input
                  type="date"
                  className="form-control"
                  name="fechaElaboracion"
                  value={form.fechaElaboracion}
                  onChange={handleFormChange}
                />
              </div>
              )}
              {showFechaVencimiento && (
              <div className="col-md-2">
                <label className="form-label">Fecha vencimiento</label>
                <input
                  type="date"
                  className="form-control"
                  name="fechaVencimiento"
                  value={form.fechaVencimiento}
                  onChange={handleFormChange}
                />
              </div>
              )}
              <div className="col-md-2">
                <label className="form-label">ID</label>
                <input
                  type="text"
                  className="form-control"
                  name="idInterno"
                  value={form.idInterno}
                  onChange={handleFormChange}
                />
              </div>
              {showTipoEmbalaje && (
              <div className="col-md-2">
                <label className="form-label">Tipo embalaje</label>
                <input
                  type="text"
                  className="form-control"
                  name="tipoEmbalaje"
                  value={form.tipoEmbalaje}
                  onChange={handleFormChange}
                />
              </div>
              )}
            </div>

            <div className="row mb-2">
              {showPesoMedidas && (
              <div className="col-md-2">
                <label className="form-label">Peso / Medidas</label>
                <input
                  type="text"
                  className="form-control"
                  name="pesoMedidas"
                  value={form.pesoMedidas}
                  onChange={handleFormChange}
                />
              </div>
              )}
              <div className="col-md-2">
                <label className="form-label">Nro. pallet (base)</label>
                <input
                  type="text"
                  className="form-control"
                  name="nroPallet"
                  value={form.nroPallet}
                  onChange={handleFormChange}
                />
              </div>
              {showUnidadesPallet && (
              <div className="col-md-2">
                <label className="form-label">Unidades por pallet</label>
                <input
                  type="number"
                  className="form-control"
                  name="unidadesPorPallet"
                  min={1}
                  required
                  value={form.unidadesPorPallet}
                  onChange={handleFormChange}
                />
              </div>
              )}
              {showPrecinto && (
              <div className="col-md-2">
                <label className="form-label">Recinto SENASA</label>
                <input
                  type="text"
                  className="form-control"
                  name="recintoSenasa"
                  value={form.recintoSenasa}
                  onChange={handleFormChange}
                />
              </div>
              )}
              <div className="col-md-2">
                <label className="form-label">Este ingreso pesa?</label>
                <select
                  className="form-select"
                  name="ingresoPesa"
                  value={form.ingresoPesa}
                  onChange={handleFormChange}
                >
                  <option value="no">No</option>
                  <option value="si">Si</option>
                </select>
              </div>
              {showCondicion && (
              <div className="col-md-2">
                <label className="form-label">Condicion</label>
                <select
                  className="form-select"
                  name="condicion"
                  value={form.condicion}
                  onChange={handleFormChange}
                >
                  <option value="OK">OK</option>
                  <option value="ROTAS">Rotas</option>
                </select>
              </div>
              )}
            </div>

            <div className="row mb-2">
              {showObservaciones && (
                <div className="col-md-8">
                  <label className="form-label">Observaciones de ingreso</label>
                  <input
                    type="text"
                    className="form-control"
                    name="observacionesIngreso"
                    value={form.observacionesIngreso}
                    onChange={handleFormChange}
                  />
                </div>
              )}
              <div className="col-md-2 d-flex align-items-end">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="mantenerDatos"
                    checked={mantenerDatos}
                    onChange={handleMantenerDatosChange}
                  />
                  <label className="form-check-label" htmlFor="mantenerDatos">
                    Mantener datos
                  </label>
                </div>
              </div>
              <div className="col-md-2">
                <label className="form-label">Replicar ingreso por</label>
                <input
                  type="number"
                  className="form-control"
                  min={1}
                  value={replicarPor ?? ""}
                  onChange={handleReplicarChange}
                />
                </div>
            </div>
          </div>

          <div className="card-footer text-end">
            <button type="submit" className="btn btn-primary" disabled={!clienteSel}>
              Agregar pallets
            </button>
          </div>
        </div>
      </form>
      )}

      {/* TABLA DE PALLETS (tipo Excel) */}
      {pallets.length > 0 && (
        <div className="card mb-3">
          <div className="card-header d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <span>Pallets ingresados ({pallets.length})</span>
            <div className="modificar-bulk-vto">
              <label htmlFor="fecha-vencimiento-masiva">
                Fecha de vencimiento para todos
              </label>
              <input
                id="fecha-vencimiento-masiva"
                type="date"
                className="form-control form-control-sm"
                value={fechaVencimientoMasiva}
                onChange={(e) => setFechaVencimientoMasiva(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                disabled={!fechaVencimientoMasiva}
                onClick={handleAplicarFechaVencimientoTodos}
              >
                Aplicar a todos
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive modificar-pallets-compact-table">
              <table className="table table-sm table-striped mb-0 tabla-pallets">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Nro. pallet</th>
                    <th>Unid. x pallet</th>
                    <th>Condicion</th>
                    <th>Dup. pallet</th>
                    <th>Cuenta pos.</th>
                    <th>Detalle</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pallets.map((p, index) => (
                    <tr key={p.palletId || `nuevo-${index}`}>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={p.idInterno}
                          onChange={(e) =>
                            handlePalletChange(index, "idInterno", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          id={`pallet-${index}`}
                          value={p.nroPallet}
                          onChange={(e) =>
                            handlePalletChange(index, "nroPallet", e.target.value)
                          }
                          onKeyDown={(e) => handleEnterPallet(e, index)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          min={1}
                          value={p.unidadesPorPallet}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "unidadesPorPallet",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={p.condicion}
                          onChange={(e) =>
                            handlePalletChange(index, "condicion", e.target.value)
                          }
                        >
                          <option value="OK">OK</option>
                          <option value="ROTAS">Rotas</option>
                        </select>
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={p.duplicarPallet}
                          onChange={() =>
                            handlePalletCheckbox(index, "duplicarPallet")
                          }
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={p.cuentaPosicion !== false}
                          onChange={() =>
                            handlePalletCheckbox(index, "cuentaPosicion")
                          }
                        />
                      </td>
                      <td className="text-center">
                        {p.palletId ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-light"
                            onClick={() => setPalletDetalleId(p.palletId)}
                          >
                            Ver detalle
                          </button>
                        ) : (
                          <span className="badge text-bg-secondary">Nuevo</span>
                        )}
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm modificar-delete-btn"
                          aria-label={`Eliminar pallet ${p.nroPallet || index + 1}`}
                          title="Eliminar pallet"
                          onClick={() => handleEliminarPallet(index)}
                        >
                          <FiTrash2 aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* La edicion completa ahora se realiza desde el modal individual. */}
            <div className="table-responsive d-none" aria-hidden="true">
              <table className="table table-sm table-striped mb-0 tabla-pallets">
                <thead className="table-light">
                  <tr>
                    {showDetalleProducto && (
                    <th className="col-xs">FC/Grupo/Proveedor</th>
                    )}
                    {showCodigo && (
                    <th className="col-xs">Codigo</th>
                    )}
                    {showDetalleProducto && (
                    <th className="col-xs">Detalle / Nro. FC</th>
                    )}
                    {showDetalleProducto && (
                    <th className="col-xxs">Cant x FC</th>
                    )}
                    {showLote && (
                    <th className="col-xxs">Nro. lote</th>
                    )}
                    {showFechaElaboracion && (
                    <th className="col-s">F. elaborac.</th>
                    )}
                    {showFechaVencimiento && (
                    <th className="col-s">F. vencim.</th>
                    )}
                    <th className="col-xxs">ID</th>
                    {showTipoEmbalaje && (
                    <th className="col-xs">Tipo embalaje</th>
                    )}
                    {showPesoMedidas && (
                    <th className="col-xs">Peso/medidas</th>
                    )}
                    <th className="col-xs">Nro. pallet</th>
                    {showUnidadesPallet && (
                    <th className="col-xs">Unid. x pallet</th>
                    )}
                    {showCondicion && (
                    <th className="col-xs">Condicion</th>
                    )}
                    {showCondicion && (
                    <th className="col-xs">Motivo daño in</th>
                    )}
                    <th className="col-xxs">Dup. pallet</th>
                    <th className="col-xxs">Cuenta pos.</th>
                    <th className="col-delete"></th>
                  </tr>
                </thead>
                <tbody>
                  {pallets.map((p, index) => (
                    <tr key={index}>
                      {showDetalleProducto && (
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={p.fcGrupoProveedor}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "fcGrupoProveedor",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      )}
                      {showCodigo && (
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={p.codigo}
                          onChange={(e) =>
                            handlePalletChange(index, "codigo", e.target.value)
                          }
                        />
                      </td>
                      )}
                      {showDetalleProducto && (
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm mb-1"
                          placeholder="Detalle"
                          value={p.detalleProducto}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "detalleProducto",
                              e.target.value
                            )
                          }
                        />
                        {/* <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Nro. FC"
                          value={p.nroFc}
                          onChange={(e) =>
                            handlePalletChange(index, "nroFc", e.target.value)
                          }
                        /> */}
                      </td>
                      )}
                      {showDetalleProducto && (
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={p.cantPorFc}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "cantPorFc",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      )}
                      {showLote && (
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={p.lote}
                          onChange={(e) =>
                            handlePalletChange(index, "lote", e.target.value)
                          }
                        />
                      </td>
                      )}
                      {showFechaElaboracion && (
                      <td>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={p.fechaElaboracion}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "fechaElaboracion",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      )}
                      {showFechaVencimiento && (
                      <td>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={p.fechaVencimiento}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "fechaVencimiento",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      )}
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={p.idInterno}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "idInterno",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      {showTipoEmbalaje && (
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={p.tipoEmbalaje}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "tipoEmbalaje",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      )}
                      {showPesoMedidas && (
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={p.pesoMedidas}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "pesoMedidas",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      )}
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          id={`pallet-${index}`}
                          value={p.nroPallet}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "nroPallet",
                              e.target.value
                            )
                          }
                          onKeyDown={(e) => handleEnterPallet(e, index)}
                        />
                      </td>
                      {showUnidadesPallet && (
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          min={1}
                          value={p.unidadesPorPallet}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "unidadesPorPallet",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      )}
                      {showCondicion && (
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={p.condicion}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "condicion",
                              e.target.value
                            )
                          }
                        >
                          <option value="OK">OK</option>
                          <option value="ROTAS">Rotas</option>
                        </select>
                      </td>
                      )}
                      {showCondicion && (
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={p.motivoDanioIn}
                          onChange={(e) =>
                            handlePalletChange(
                              index,
                              "motivoDanioIn",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      )}
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={p.duplicarPallet}
                          onChange={() =>
                            handlePalletCheckbox(index, "duplicarPallet")
                          }
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                        checked={p.cuentaPosicion !== false}
                          onChange={() =>
                            handlePalletCheckbox(index, "cuentaPosicion")
                          }
                        />
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm modificar-delete-btn"
                          aria-label={`Eliminar pallet ${p.nroPallet || index + 1}`}
                          title="Eliminar pallet"
                          onClick={() => handleEliminarPallet(index)}
                        >
                          <FiTrash2 aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card-footer mt-3">
            <div>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Lote</th>
                    <th>Cantidad pallets</th>
                    <th>Total unidades</th>
                  </tr>
                </thead>
                <tbody>
                  {getResumenPorLote().map((r) => (
                    <tr key={r.lote}>
                      <td>{r.lote}</td>
                      <td>{r.cantidadPallets}</td>
                      <td>{r.totalUnidades}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <hr />
              <p>
                <strong>Total pallets:</strong> {totalPallets}
              </p>
              <p>
                <strong>Total unidades:</strong> {totalUnidades}
              </p>
            </div>
            <div className="text-end">
              <button
                type="button"
                className="btn btn-success"
                onClick={abrirModalResumen}
              >
                Finalizar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DETALLE COMPLETO DE PALLETS YA GUARDADOS */}
      {palletDetalle && palletDetalleIndex >= 0 && (
        <div
          className="modal fade show d-block modificar-detalle-modal"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="detallePalletsTitle"
          style={{ backgroundColor: "rgba(0,0,0,0.68)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title" id="detallePalletsTitle">
                    Detalle del pallet {palletDetalle.nroPallet || palletDetalle.idInterno}
                  </h5>
                  <div className="small text-muted">
                    {form.nroRemito} · {form.cliente}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Cerrar"
                  onClick={() => setPalletDetalleId(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="estado-msg info">
                  Puede editar estos valores. Los cambios se guardan al finalizar
                  la modificacion del ingreso.
                </div>
                <section className="modificar-pallet-detail-card">
                  <div className="modificar-pallet-detail-form">
                    {getPalletDetailItems().map(
                      ({ label, field, type = "text", min }) => (
                        <label className="modificar-pallet-detail-field" key={field}>
                          <span>{label}</span>
                          {type === "boolean" ? (
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={Boolean(palletDetalle[field])}
                              onChange={() =>
                                handlePalletCheckbox(palletDetalleIndex, field)
                              }
                            />
                          ) : type === "readonlyBoolean" ? (
                            <strong>{palletDetalle[field] ? "Si" : "No"}</strong>
                          ) : type === "condition" ? (
                            <select
                              className="form-select"
                              value={palletDetalle[field] || "OK"}
                              onChange={(e) =>
                                handlePalletChange(
                                  palletDetalleIndex,
                                  field,
                                  e.target.value
                                )
                              }
                            >
                              <option value="OK">OK</option>
                              <option value="ROTAS">Rotas</option>
                            </select>
                          ) : (
                            <input
                              type={type}
                              className="form-control"
                              min={min}
                              value={palletDetalle[field] ?? ""}
                              onChange={(e) =>
                                handlePalletChange(
                                  palletDetalleIndex,
                                  field,
                                  e.target.value
                                )
                              }
                            />
                          )}
                        </label>
                      )
                    )}
                  </div>
                </section>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setPalletDetalleId(null)}
                >
                  Listo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE RESUMEN */}
      {mostrarModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Resumen por lote</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cerrarModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="estado-msg warn">
                  Se van a modificar los datos de este ingreso. Verifique antes
                  de guardar.
                </div>
                {modalError && (
                  <div className="estado-msg error" role="alert">
                    {modalError}
                  </div>
                )}
                {pallets.length === 0 ? (
                  <p>No hay pallets para mostrar.</p>
                ) : (
                  <>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Lote</th>
                          <th>Cantidad pallets</th>
                          <th>Total unidades</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getResumenPorLote().map((r) => (
                          <tr key={r.lote}>
                            <td>{r.lote}</td>
                            <td>{r.cantidadPallets}</td>
                            <td>{r.totalUnidades}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <hr />
                    <p>
                      <strong>Total pallets:</strong> {totalPallets}
                    </p>
                    <p>
                      <strong>Total unidades:</strong> {totalUnidades}
                    </p>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cerrarModal}
                >
                  Cerrar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={guardando}
                  onClick={handleCompletarIngreso}
                >
                  {guardando ? "Guardando..." : "Confirmar y guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {mostrarFiltrosModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Filtros del cliente</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setMostrarFiltrosModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {filtrosSel?.length ? (
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {filtrosSel.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                ) : (
                  <p>El cliente no tiene filtros configurados.</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setMostrarFiltrosModal(false)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModificacionIngreso;



